"use client";

import { Button } from "@fanduel/formation-react-components";
import { useState } from "react";
import {
  HEAD_TO_HEAD_LINE_FILTER_APPLIED,
  HEAD_TO_HEAD_STATUS_MAP,
} from "@/constants/amplitude";
import { OddsSelection } from "@/graphql/gql/graphql";
import { useAmplitude } from "@/utils/hooks/useAmplitude";
import { useHeadToHeadContext } from "@/utils/hooks/useHeadToHeadContext";
import { EmptyMessage } from "../PlayerStats/PlayerProps/oddsUnavailable/OddsUnavailable";
import { TeamDisplay } from "./LineMovement";
import LinePlot, { LinePlotData } from "./LinePlot";
import SingleMarketTeam from "./SingleMarketTeam";
import styles from "./lineChart.module.scss";

export enum MarketName {
  Spread = "Spread",
  Moneyline = "Moneyline",
  Total = "Total",
}

export type Team = {
  color: string;
  name: string;
  line?: LinePlotData;
  odds: LinePlotData;
  oddsButtonData?: OddsSelection | null;
};

interface SingleMarketCardProps {
  days: LinePlotData;
  home: Team;
  market: MarketName;
  show: TeamDisplay;
  away: Team;
  gameId: number;
  showToggle?: boolean;
}

const SingleMarketCard = ({
  days,
  home,
  away,
  market,
  show,
  gameId,
  showToggle = false,
}: SingleMarketCardProps) => {
  if (market === MarketName.Total) {
    show = TeamDisplay.Both;
  }
  const showHome = show === TeamDisplay.Home || show === TeamDisplay.Both;
  const showAway = show === TeamDisplay.Away || show === TeamDisplay.Both;
  const [toggle, setToggle] = useState(() => market !== MarketName.Moneyline);

  const { logAmplitudeEvent } = useAmplitude();
  const { eventName, gameState } = useHeadToHeadContext();
  const handleClick = (toggle: boolean) => {
    setToggle(toggle);
    logAmplitudeEvent(HEAD_TO_HEAD_LINE_FILTER_APPLIED, {
      "Filter Name": market,
      "Filter Value": toggle ? market : "Odds",
      Module: "line_movement",
      "Event Name": eventName,
      "Page State": HEAD_TO_HEAD_STATUS_MAP[gameState],
    });
  };
  const lineIsUnavailable = !(home.odds?.[0] != null && away.odds?.[0] != null);

  return (
    <div className={styles.singleMarketCard}>
      <div className={styles.heading}>
        <h3>{market}</h3>
        {showToggle && (
          <div className={styles.toggle}>
            <Button
              className={styles.toggleButton}
              size="small"
              variant={!toggle ? "link" : "secondary"}
              onClick={() => handleClick(true)}
              label={market}
            />
            <Button
              className={styles.toggleButton}
              size="small"
              variant={toggle ? "link" : "secondary"}
              onClick={() => handleClick(false)}
              label="Odds"
            />
          </div>
        )}
      </div>
      <div className={styles.infoAndGraph}>
        <div className={styles.teamsInfo}>
          {showAway && (
            <SingleMarketTeam market={market} team={away} toggle={toggle} />
          )}
          {showHome && (
            <SingleMarketTeam market={market} team={home} toggle={toggle} />
          )}
        </div>
        <div className={styles.linePlotContainer}>
          {lineIsUnavailable ? (
            <EmptyMessage className={styles.emptyMessage} />
          ) : null}
          <LinePlot
            time={days}
            oddsSuggestedSegments={5}
            home={home}
            away={away}
            market={market}
            gameId={gameId}
            toggle={toggle}
            show={show}
          />
        </div>
      </div>
    </div>
  );
};

export default SingleMarketCard;
